import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import type {
  IGradeQueryParams,
  IGradeDetailResponse,
  IGrade,
  IGetGradeDetailsRequestParams,
  IMasterGrade,
  IMasterGradeReqBody,
  IGradeDetail,
  IGradeDeleteSuccessResponse,
  IGradeDeleteGatedResponse,
  IGradeInactivateGatedResponse,
} from '../../interface/gradeModel';
import { ICreateSubjectError, ICreateSubjectResponseBody } from '../../interface/subjectModel';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { IErrorResponse } from '../../interface';
import { RootState } from '../store';
import { STATUS } from '../../constants';
import qs from 'qs';

interface IGradeState {
  loading: boolean;
  error?: IErrorResponse;
  message?: string;
  grade?: IGrade[];
  selectedGradeDetail?: IGradeDetail;
  description?: string;
  status?: string;
  ForgetPasswordToken?: string;
}

const DEFAULT_STATE: IGradeState = {
  grade: undefined,
  selectedGradeDetail: undefined,
  description: undefined,
  loading: false,
  error: undefined,
  message: undefined,
  status: undefined,
  ForgetPasswordToken: undefined,
};

export const getgrade = createAsyncThunk<
  IMasterGrade,
  IGetGradeDetailsRequestParams,
  { rejectValue: ICreateSubjectError }
>('grade/getGrade', async (params, { rejectWithValue }) => {
  try {
    // Smart defaults: undefined = use default, true/false = filter, null = omit filter
    const isActive = params?.isActive !== undefined ? params.isActive : true;

    // Build query params with proper typing
    const queryParams: IGradeQueryParams = {
      search: params?.search,
      page: params?.page,
      pageSize: params?.pageSize,
      curriculumId: params?.curriculumId,
    };

    // Add filters conditionally (null = omit from query)
    if (isActive !== null) {
      queryParams.isActive = isActive ? 'true' : 'false';
    }

    const response = await api.get('/grade', {
      params: queryParams,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error',
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

// New thunk to fetch grade details with classrooms
export const fetchGradeDetail = createAsyncThunk<
  IGradeDetailResponse,
  string, // ID parameter
  { rejectValue: ICreateSubjectError }
>('grade/fetchGradeDetail', async (id, { rejectWithValue }) => {
  try {
    const response = await api.get(`/grade/${id}`);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error',
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const getReport = createAsyncThunk<
  void, // No payload is returned because it's a download
  void, // No parameters are passed
  { rejectValue: ICreateSubjectError } // Rejected value type for errors
>('grade/getReport', async (_, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<ArrayBuffer> = await api.get(`/grade/download`, {
      responseType: 'arraybuffer', // Ensure the response is treated as binary
    });

    // Call the download handler
    handleDownload(response);

    return; // Nothing is returned because it's a side-effect action
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error',
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

const handleDownload = (response: AxiosResponse<ArrayBuffer>): void => {
  // Create an object URL from the response data (which is an ArrayBuffer)
  const fileUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })); // Create a link element
  const link = document.createElement('a'); // Set the link's href to the object URL created from the response data
  link.href = fileUrl; // Set the filename for the download
  link.download = 'report.pdf'; // Programmatically trigger a click on the link to start the download
  link.click();
  URL.revokeObjectURL(fileUrl);
};

export const creategrade = createAsyncThunk<
  ICreateSubjectResponseBody,
  IMasterGradeReqBody,
  { rejectValue: ICreateSubjectError }
>('grade/creategrade', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post('/grade', body);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error',
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const createStatus = createAsyncThunk<
  ICreateSubjectResponseBody,
  { id: string; status: boolean; confirmed?: boolean },
  { rejectValue: IGradeInactivateGatedResponse | { message: string } }
>('grade/updateStatus', async ({ id, status, confirmed }, { rejectWithValue }) => {
  try {
    const response = await api.put(`/grade/status/${id}`, { status, confirmed });
    return response.data;
  } catch (err: any) {
    // The shared axios instance rejects with the response BODY directly (see the
    // interceptor in services/api.tsx), so `err` is already the parsed
    // { status, message, errorCode, data } envelope — NOT an AxiosError. Surface
    // the structured soft-gate body so the component can branch on errorCode/data.
    const body = err as Partial<IGradeInactivateGatedResponse> & { message?: string };
    if (body?.errorCode) {
      return rejectWithValue(body as IGradeInactivateGatedResponse);
    }
    return rejectWithValue({ message: body?.message || 'Failed to update grade status' });
  }
});

export const updateGrade = createAsyncThunk<
  ICreateSubjectResponseBody,
  { body: IMasterGradeReqBody; id: string },
  { rejectValue: ICreateSubjectError }
>('grade/updateGrade', async ({ body, id }, { rejectWithValue }) => {
  try {
    const response = await api.put(`/grade/${id}`, body);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: 'Unknown error' });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const deleteGrade = createAsyncThunk<
  IGradeDeleteSuccessResponse,
  { id: string; confirmed?: boolean },
  { rejectValue: IGradeDeleteGatedResponse | { message: string } }
>('grade/deleteGrade', async ({ id, confirmed }, { rejectWithValue }) => {
  try {
    // A DELETE body must be sent via the axios `data` config key.
    const response = await api.delete(`/grade/${id}`, { data: { confirmed } });
    return response.data as IGradeDeleteSuccessResponse;
  } catch (err: any) {
    // The shared axios instance rejects with the response BODY directly, so `err`
    // is already the parsed { status, message, errorCode, data } envelope. Pass a
    // structured gate body through (hard block or soft gate) so the component can
    // read errorCode + impact `data` and show the correct modal.
    const body = err as Partial<IGradeDeleteGatedResponse> & { message?: string };
    if (body?.errorCode) {
      return rejectWithValue(body as IGradeDeleteGatedResponse);
    }
    return rejectWithValue({ message: body?.message || 'Failed to delete grade' });
  }
});

const gradeSlice = createSlice({
  name: 'grade',
  initialState: DEFAULT_STATE,
  reducers: {
    resetState: () => DEFAULT_STATE,
    // Allows clearing or setting the currently selected grade detail
    setSelectedGradeDetail: (state, action: PayloadAction<IGradeDetail | undefined>) => {
      state.selectedGradeDetail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getgrade.pending, (state) => {
      state.status = STATUS.LOADING;
      state.loading = true;
    });
    builder.addCase(getgrade.fulfilled, (state, action) => {
      state.grade = action.payload.data.grades;
      state.message = action.payload.message;
      state.status = STATUS.SUCCEEDED;
      state.loading = false;
    });
    builder.addCase(getgrade.rejected, (state, action: PayloadAction<IErrorResponse>) => {
      state.error = action.payload;
      state.description = action.payload.message; // Use message for UI
      state.message = action.payload.message;
      state.status = STATUS.FAILED;
      state.loading = false;
    });

    builder.addCase(fetchGradeDetail.pending, (state) => {
      state.status = STATUS.LOADING;
      state.loading = true;
    });
    builder.addCase(fetchGradeDetail.fulfilled, (state, action) => {
      state.selectedGradeDetail = action.payload.data?.grade;
      state.message = action.payload.message;
      state.status = STATUS.SUCCEEDED;
      state.loading = false;
    });
    builder.addCase(fetchGradeDetail.rejected, (state, action: PayloadAction<IErrorResponse>) => {
      state.error = action.payload;
      state.description = action.payload.message;
      state.message = action.payload.message;
      state.status = STATUS.FAILED;
      state.loading = false;
    });

    builder.addCase(creategrade.pending, (state) => {
      state.status = STATUS.LOADING;
      state.loading = true;
    });
    builder.addCase(creategrade.fulfilled, (state, _action) => {
      //state.description = action.payload.data;
      //state.message = action.payload.message;
      state.status = STATUS.SUCCEEDED;
      state.loading = false;
    });
    builder.addCase(creategrade.rejected, (state, action: PayloadAction<IErrorResponse>) => {
      state.error = action.payload;
      state.description = action.payload.message; // Use message for UI
      state.status = STATUS.FAILED;
      state.loading = false;
    });

    builder.addCase(updateGrade.pending, (state) => {
      state.status = STATUS.LOADING;
      state.loading = true;
    });
    builder.addCase(updateGrade.fulfilled, (state, _action) => {
      //state.description = action.payload.description;
      //state.message = action.payload.message;
      state.status = STATUS.SUCCEEDED;
      state.loading = false;
    });
    builder.addCase(updateGrade.rejected, (state, action: PayloadAction<IErrorResponse>) => {
      state.error = action.payload;
      state.description = action.payload.message; // Use message for UI
      state.status = STATUS.FAILED;
      state.loading = false;
    });

    builder.addCase(deleteGrade.pending, (state) => {
      state.status = STATUS.LOADING;
      state.loading = true;
    });
    builder.addCase(deleteGrade.fulfilled, (state, action) => {
      //state.description = action.payload.;
      state.message = action.payload.message;
      state.status = STATUS.SUCCEEDED;
      state.loading = false;
    });
    // builder.addCase(deleteGrade.rejected, (state, action: PayloadAction<IErrorResponse>) => {
    //     state.error = action.payload;
    //     state.description = action.payload.description; // Use description for UI
    //     state.status = STATUS.FAILED;
    // });
  },
});

export const { resetState, setSelectedGradeDetail } = gradeSlice.actions;
export const gradeReducer = gradeSlice.reducer;

export const selectResetPasswordState = (state: RootState): IGradeState => state.grade;
export const selectResetPasswordStatus = (state: RootState): string | undefined =>
  state.grade.status;
export const selectGrade = (state: RootState): IGrade[] | undefined => state.grade.grade;

export default gradeReducer;
